'use client';

import type { UpdateFeatureFlagRequest } from '@/@feature-flags/types/api';
import type { FeatureFlag } from '@/@feature-flags/types/entities';
import {
    Button,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Switch,
    Textarea,
} from '@heroui/react';
import { useCallback, useEffect, useState } from 'react';

interface EditFlagModalProps {
    isOpen: boolean;
    onClose: () => void;
    flag: FeatureFlag | null;
    onSubmit: (key: string, payload: UpdateFeatureFlagRequest) => Promise<unknown>;
    isSubmitting: boolean;
}

export function EditFlagModal({
    isOpen,
    onClose,
    flag,
    onSubmit,
    isSubmitting,
}: EditFlagModalProps) {
    const [description, setDescription] = useState('');
    const [enabledInternal, setEnabledInternal] = useState(false);
    const [enabledExternal, setEnabledExternal] = useState(false);

    useEffect(() => {
        if (flag) {
            setDescription(flag.description ?? '');
            setEnabledInternal(flag.enabledInternal);
            setEnabledExternal(flag.enabledExternal);
        }
    }, [flag]);

    const handleSubmit = useCallback(async () => {
        if (!flag) return;
        await onSubmit(flag.key, {
            description: description === '' ? null : description,
            enabledInternal,
            enabledExternal,
        });
        onClose();
    }, [flag, description, enabledInternal, enabledExternal, onSubmit, onClose]);

    if (!flag) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <ModalContent>
                <ModalHeader>
                    Edit Feature Flag: <code className="ml-2 text-primary">{flag.key}</code>
                </ModalHeader>
                <ModalBody className="gap-4">
                    <Textarea
                        label="Description"
                        placeholder="Human-readable description"
                        value={description}
                        onValueChange={setDescription}
                    />
                    <div className="flex flex-col gap-3">
                        <Switch
                            isSelected={enabledInternal}
                            onValueChange={setEnabledInternal}
                            size="sm"
                        >
                            <span className="text-small">Enabled for Internal Users</span>
                        </Switch>
                        <Switch
                            isSelected={enabledExternal}
                            onValueChange={setEnabledExternal}
                            size="sm"
                        >
                            <span className="text-small">Enabled for External Users</span>
                        </Switch>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button variant="flat" onPress={onClose}>
                        Cancel
                    </Button>
                    <Button color="primary" onPress={handleSubmit} isLoading={isSubmitting}>
                        Save Changes
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
